import { useEffect, useRef, type ReactElement } from 'react';

/**
 * Controles de navegación semanal del dashboard.
 *
 * - Operable con teclado nativamente (botones).
 * - `aria-live="polite"` para que screen readers anuncien el rango cuando
 *   cambia tras click.
 * - Atajos de teclado `←`/`→` cuando alguno de los controles tiene foco —
 *   coste mínimo y muy útil para navegar varias semanas seguidas.
 */
export function WeekNavigator({
  weekStartISO,
  weekEndISO,
  onChange,
}: {
  weekStartISO: string;
  weekEndISO: string;
  onChange: (newWeekStartISO: string) => void;
}): ReactElement {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        onChange(shiftWeekISO(weekStartISO, -7));
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        onChange(shiftWeekISO(weekStartISO, 7));
      }
    }
    el.addEventListener('keydown', onKey);
    return () => el.removeEventListener('keydown', onKey);
  }, [weekStartISO, onChange]);

  function goPrev() {
    onChange(shiftWeekISO(weekStartISO, -7));
  }
  function goNext() {
    onChange(shiftWeekISO(weekStartISO, 7));
  }
  function goToday() {
    onChange(startOfIsoWeekISO(new Date()));
  }

  return (
    <div
      ref={containerRef}
      role="toolbar"
      aria-label="Navegación entre semanas"
      className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-slate-200 bg-white px-3 py-2 shadow-sm"
    >
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={goPrev}
          aria-label="Semana anterior"
          className="rounded border border-slate-300 bg-white px-2 py-1 text-sm text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1"
        >
          <span aria-hidden="true">←</span>
        </button>
        <button
          type="button"
          onClick={goToday}
          className="rounded border border-slate-300 bg-white px-3 py-1 text-sm font-medium text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1"
        >
          Hoy
        </button>
        <button
          type="button"
          onClick={goNext}
          aria-label="Semana siguiente"
          className="rounded border border-slate-300 bg-white px-2 py-1 text-sm text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1"
        >
          <span aria-hidden="true">→</span>
        </button>
      </div>
      <p
        role="status"
        aria-live="polite"
        className="text-sm font-medium text-slate-900"
      >
        {formatRange(weekStartISO, weekEndISO)}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Devuelve el ISO del lunes (00:00 UTC) que contiene `date`. Usamos UTC para
 * mantener consistencia con el backend, que también define la semana en UTC
 * (la traducción a timezone local se hace por workout en `formatTime`).
 */
export function startOfIsoWeekISO(date: Date): string {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const dayOfWeek = d.getUTCDay(); // 0=dom, 1=lun, ..., 6=sab
  const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  d.setUTCDate(d.getUTCDate() + diff);
  return d.toISOString();
}

export function shiftWeekISO(weekStartISO: string, days: number): string {
  const base = new Date(weekStartISO);
  base.setUTCDate(base.getUTCDate() + days);
  return base.toISOString();
}

function formatRange(startISO: string, endISO: string): string {
  try {
    const start = new Date(startISO);
    const end = new Date(endISO);
    const sameMonth =
      start.getUTCMonth() === end.getUTCMonth() &&
      start.getUTCFullYear() === end.getUTCFullYear();
    const dayFmt = new Intl.DateTimeFormat('es-ES', { day: '2-digit', timeZone: 'UTC' });
    const monthFmt = new Intl.DateTimeFormat('es-ES', { month: 'short', year: 'numeric', timeZone: 'UTC' });
    const fullFmt = new Intl.DateTimeFormat('es-ES', {
      day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC',
    });
    if (sameMonth) {
      return `${dayFmt.format(start)}-${dayFmt.format(end)} ${monthFmt.format(end)}`;
    }
    return `${fullFmt.format(start)} — ${fullFmt.format(end)}`;
  } catch {
    return `${startISO} — ${endISO}`;
  }
}
